import React from "react";
import { StaticImage } from "gatsby-plugin-image";
import Layout from "@app/Layout";
import Canonical from "@shared/head/Canonical";
import Breadcrumbs from "@shared/head/Breadcrumbs";
import { HeroColumns } from "@shared/uiNew/HeroSections/HeroColumns";
import ButtonBlack from "@shared/uiNew/Button/button_black";
import ButtonOrange from "@shared/uiNew/Button/button_orange";

import { NarrativeSection } from "@features/about-company/NarrativeSection";
import { JoinTeamSection } from "@features/about-company/JoinTeamSection";
import { ContactsSection } from "@features/about-company/ContactsSection";

import { officesData, contactEmail } from "@shared/arrays/about-company";

import * as styles from "./about-company.module.scss";


export const Head = () => {
  const localBusinessSchema = officesData.map((office) => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Dashly — ${office.city}`,
    address: {
      "@type": "PostalAddress",
      streetAddress: office.address,
      addressLocality: office.city,
      addressCountry: office.country,
    },
    email: contactEmail,
    url: "https://www.dashly.io/",
    parentOrganization: {
      "@type": "Organization",
      name: "Dashly",
      url: "https://www.dashly.io/",
    },
  }));

  return (
    <>
      <title>About us | Dashly</title>
      <meta
        name="description"
        content="Join us to change the traditional ways Marketing, Sales, and Support interact with Customers."
      />
      <Canonical url="https://www.dashly.io/about-company/" />
      <Breadcrumbs
        list={[
          { name: "Dashly", url: "https://www.dashly.io/" },
          { name: "About us", url: "https://www.dashly.io/about-company/" },
        ]}
      />
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
    </>
  );
};

export default function AboutCompany({ location }: { location: Location }) {
  return (
    <Layout location={location} className="new-design">

      {/* ── Hero: CEO intro ───────────────────────────────────── */}
      <div className={styles.heroWrap}>
      <HeroColumns
        tag={<span>About us</span>}
        title="Hi, my name is Dmitry. I am Dashly's CEO and Co-Founder"
        subTitle="I am eager to share our vision and mission — why we do what we do and what drives us."
        button={
          <ButtonOrange variant="primary" icon="arrow" text="Book a demo" />
        }
        img={
          <StaticImage
            src="../../images/about-us/_.png"
            alt="Dmitry Sergeev — CEO and Co-Founder of Dashly"
            placeholder="blurred"
          />
        }
      />
      </div>

      {/* ── Chapter 1: How communication changed the world ────── */}
      <NarrativeSection
        heading="Chapter 1 — How communication has been changing the world"
        rows={[
          {
            tag: "Tigers, humans and words",
            title: "Homo Sapience won because they could communicate better",
            text: "Communication as a phenomenon is terrific. The primary shifts in human history took place because of the changes in the ways people interacted with each other.\n\nDo you know why a species known as Homo Sapience won the battle of human survival? Because humans could explain what they want better than the other species! Neanderthals could say, \"A tiger is there!\" Meanwhile, Humans could tell where the tiger was at the moment, where it had been, and where the tiger might head in the future.\n\nAt first glance, such an insignificant difference helped Homo Sapience stand at the top of the food chain. This is hilarious because when we consider other \"characteristics,\" humans lose to almost all the other species on Earth. Even to ants!",
            img: (
              <StaticImage
                src="../../images/about-us/dashly_-_tiger.png"
                alt="Homo Sapiens drawing on cave wall — the birth of human communication"
                placeholder="blurred"
              />
            ),
          },
          {
            tag: "Gutenberg's press",
            title: "The printing press turned the world upside down",
            text: "The invention of the printing press was a pivotal moment and another great story. The printing press turned the world upside down and became a symbol of the Modern era.\n\nInteresting fact — the printing press invented in the 1440s, remained almost unchanged till the 19th century. The invention process was not easy. Gutenberg was loaded with loans and litigations; sometimes, he even did not have experienced workers and suitable tools. Nevertheless, he did not give up and made the process of book mass production real.\n\nThe new printing press dramatically decreased the cost of production and made books available for a bigger audience. Gutenberg's invention speeded up the spread of knowledge and, as a result, impacted religion, education, and culture in Europe.",
            img: (
              <StaticImage
                src="../../images/about-us/dashly_-_gutenberg.png"
                alt="Gutenberg with his printing press"
                placeholder="blurred"
              />
            ),
            flip: true,
          },
          {
            tag: "World Wide Web",
            title: "Tim Berners-Lee made communication faster than ever",
            text: "Another fascinating story — the story of Sir Timothy John Berners-Lee. He made the communication and spread of information even faster, much faster.\n\nIn 1989 Tim replaced the linear text with hypertext. Being a scientist at CERN, he proposed and later prototyped a new hypertext project — he called it \"WorldWideWeb\". In 1993 WWW became public.\n\nYou all know the outcomes, and this is super inspiring! Everything about communications is exciting. We genuinely want to impact the way people interact, exchange information, and, in the end, the way they feel. We want to change the communication between Companies and Humans.",
            img: (
              <StaticImage
                src="../../images/about-us/dashly_-_www.png"
                alt="Tim Berners-Lee — inventor of the World Wide Web"
                placeholder="blurred"
              />
            ),
          },
        ]}
      />

      {/* ── Chapter 2: The big issue ──────────────────────────── */}
      <NarrativeSection
        heading="Chapter 2 — The big issue"
        rows={[
          {
            tag: "2020 — Changes in the way we communicate",
            title: "Company to Customer communication is stuck in the 2000s",
            text: "Remember yourself ten years ago, or even five years ago. You used to call your family and friends by phone, dialed phone numbers, used address books. Now we primarily use digital channels where we text, not call. We are more flexible — we can start with a call, switch to video, sending pictures simultaneously.\n\nSadly, the communication between companies and their customers seems to be stuck in the 2000s. We are 100% sure that most of the issues can be fixed — when the communication with the Customer becomes the focal point of processes in Marketing, Sales, and Support.",
            img: (
              <StaticImage
                src="../../images/about-us/image.png"
                alt="Customers reaching out through many channels while companies struggle to keep up"
                placeholder="blurred"
              />
            ),
          },
          {
            tag: "Losing data is a total failure",
            title: "You ask Department A. Department B knows nothing.",
            text: "Imagine the situation — you ask Department A a question. Later you realize that Department B can help, and... department B knows nothing about your question until you describe your situation to them. The same is about Department C and so on. You can even think that all the departments are on different planets.\n\nIt is a no-win situation. The customer experience is awful.",
            img: (
              <StaticImage
                src="../../images/about-us/Data_loss_Dashly.png"
                alt="Data scattered across disconnected channels — the root of broken customer experience"
                placeholder="blurred"
              />
            ),
            flip: true,
          },
          {
            tag: "Borderless communication",
            title: "The value of successful communication",
            text: "You decide to contact a company. Once you send them an email, you will likely prefer emails for the future. If you call, all of your future communications will stay on the phone. However, you may want to switch to WhatsApp next time — and then the company's representative has no idea what you were discussing before.\n\nNow, remember how you text with friends. Using numerous channels at the same time is something natural. You are always in context. No one loves filling in those forms with infinite fields. No one loves waiting for a callback. Communication is the key to a successful experience, a brand with loyal fans, and long-term relationships with a customer.",
            img: (
              <StaticImage
                src="../../images/about-us/Communication_is_the.png"
                alt="Seamless communication between people — what customer experience should feel like"
                placeholder="blurred"
              />
            ),
          },
        ]}
      />

      {/* ── Chapter 3: AI agents changed the game ────────────── */}
      <NarrativeSection
        heading="Chapter 3 — AI agents changed the rules of the game"
        footer={
          <ButtonBlack
            variant="outline"
            icon="arrow"
            text="Read: AI Inbound Revenue Agents"
            href="/ai-inbound-revenue-agents/"
          />
        }
        rows={[
          {
            tag: "The broken inbound funnel",
            title: "70% of buyers choose whoever responds first. The average response time is 42 hours.",
            text: "The traditional funnel was built as a chain of handoffs. Marketing drives traffic to the website. SDRs capture leads, send follow-ups, qualify manually. AEs run discovery calls. At every step, time passes. Leads go cold.\n\nThis isn't a headcount problem. Hiring more SDRs doesn't fix a 2am lead who found you through organic search and never heard back. The funnel was built around human speed — and humans simply aren't fast enough.",
            img: (
              <StaticImage
                src="../../images/about-us/Communication_withou.png"
                alt="Broken inbound funnel — leads going cold while waiting in a queue"
                placeholder="blurred"
              />
            ),
          },
          {
            tag: "The shift",
            title: "An AI Revenue Agent handles the entire journey — from first touch to booked meeting",
            text: "AI Inbound Revenue Agents aren't chatbots or FAQ widgets. They perceive context, reason about intent, and act across every channel. They load the visitor's full history — pages viewed, CRM data, email opens — before saying hello.\n\nThey qualify leads, answer questions, and book meetings in minutes, not hours. No queue. No handoffs. No gaps. The same quality conversation at 3pm and at 3am.\n\nMarketing focuses on campaigns. Sales focuses on closing. AI owns the first mile.",
            img: (
              <StaticImage
                src="../../images/about-us/Group_1893508264.png"
                alt="AI Revenue Agent — instant, context-aware, 24/7 across every channel"
                placeholder="blurred"
              />
            ),
            flip: true,
          },
        ]}
      />

      {/* ── Mission ───────────────────────────────────────────── */}
      <section className={styles.mission}>
        <div className={styles.missionInner}>
          <div className={styles.missionImg}>
            <StaticImage
              src="../../images/about-us/Communications_Dashl.png"
              alt="Dashly — connecting people across every communication channel"
              placeholder="blurred"
            />
          </div>
          <div className={styles.missionText}>
            <span className={styles.missionTag}>Our mission</span>
            <h2 className={styles.missionHeading}>
              We want to make communication between Companies and Customers easy
              and seamless
            </h2>
            <div className={styles.missionBody}>
              <p>
                Like everyday human-to-human interactions, by putting customer
                communication in the company's center.
              </p>
              <p>
                Therefore, we change the traditional ways Marketing, Sales, and
                Support interact with Customers.
              </p>
            </div>
            <p className={styles.missionCta}>Join us! ✋</p>
          </div>
        </div>
      </section>

      {/* ── Join our team ─────────────────────────────────────── */}
      <JoinTeamSection />

      {/* ── Offices & Contacts ───────────────────────────────── */}
      <ContactsSection />

    </Layout>
  );
}
